<script setup>
import { computed, ref, watch, getCurrentInstance } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import axios from 'axios';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    templateType: {
        type: String,
        default: ''
    },
    newsletterId: {
        type: Number,
        default: null
    },
    language: {
        type: String,
        default: 'en'
    },
    availableLanguages: {
        type: Array,
        default: () => ['pt', 'en', 'es']
    }
});

const emit = defineEmits(['update:modelValue']);

const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

const activeTab = ref(0);
const loading = ref({ pt: false, en: false, es: false });
const error = ref({ pt: null, en: null, es: null });
const previewHtml = ref({ pt: '', en: '', es: '' });

const languages = ['pt', 'en', 'es'];

const templateMap = {
    'Discount': 'discount',
    'Best Sellers': 'best-sellers',
    'Highlights': 'highlights',
    'Custom': 'custom'
};

const loadPreview = async (lang) => {
    if (!props.templateType) return;
    
    loading.value[lang] = true;
    error.value[lang] = null;
    previewHtml.value[lang] = '';
    
    try {
        const templateKey = templateMap[props.templateType];
        if (!templateKey) {
            throw new Error($t('Template type not found'));
        }
        
        const response = await axios.get(`/admin/newsletter/preview/${templateKey}`, {
            params: {
                newsletter_id: props.newsletterId,
                language: lang
            }
        });
        previewHtml.value[lang] = response.data.html;
    } catch (err) {
        console.error($t('Error loading preview') + ':', err);
        error.value[lang] = err.response?.data?.message || err.message || $t('Unknown error');
    } finally {
        loading.value[lang] = false;
    }
};

const onTabChange = (event) => {
    const lang = languages[event.index];
    if (!previewHtml.value[lang] && !loading.value[lang] && !error.value[lang]) {
        loadPreview(lang);
    }
};

const handleClose = () => {
    visible.value = false;
};

watch(() => [props.modelValue, props.templateType], ([isVisible, templateType]) => {
    if (isVisible && templateType) {
        const currentLang = languages[activeTab.value];
        loadPreview(currentLang);
    }
}, { immediate: true });
</script>

<template>
    <Dialog 
        v-model:visible="visible" 
        modal 
        :header="`Preview - ${templateType}`" 
        :style="{width: '70rem'}"
        :breakpoints="{'1199px': '85vw', '575px': '95vw'}"
        class="preview-modal"
    >
        <template #default>
            <div class="preview-container">
                <TabView v-model:activeIndex="activeTab" @tab-change="onTabChange">
                    <TabPanel :header="$t('Portuguese')" :disabled="!props.availableLanguages.includes('pt')">
                        <div v-if="loading.pt" class="text-center py-8">
                            <i class="pi pi-spin pi-spinner text-3xl text-blue-500"></i>
                            <p class="mt-3 text-gray-600">{{ $t('Loading preview...') }}</p>
                        </div>
                        
                        <div v-else-if="error.pt" class="text-center py-8">
                            <i class="pi pi-exclamation-triangle text-3xl text-red-500"></i>
                            <p class="mt-3 text-red-600">{{ $t('Error loading preview') }}: {{ error.pt }}</p>
                            <Button 
                                :label="$t('Try Again')" 
                                class="p-button-sm mt-3"
                                @click="loadPreview('pt')"
                            />
                        </div>
                        
                        <div v-else-if="previewHtml.pt" 
                             class="email-preview-content" 
                             v-html="previewHtml.pt">
                        </div>
                        
                        <div v-else class="text-center py-8">
                            <i class="pi pi-info-circle text-3xl text-gray-400"></i>
                            <p class="mt-3 text-gray-600">{{ $t('Template not configured yet.') }}</p>
                            <p class="text-sm text-gray-500">{{ $t('Configure the template first to see preview.') }}</p>
                        </div>
                    </TabPanel>

                    <TabPanel :header="$t('English')" :disabled="!props.availableLanguages.includes('en')">
                        <div v-if="loading.en" class="text-center py-8">
                            <i class="pi pi-spin pi-spinner text-3xl text-blue-500"></i>
                            <p class="mt-3 text-gray-600">{{ $t('Loading preview...') }}</p>
                        </div>
                        
                        <div v-else-if="error.en" class="text-center py-8">
                            <i class="pi pi-exclamation-triangle text-3xl text-red-500"></i>
                            <p class="mt-3 text-red-600">{{ $t('Error loading preview') }}: {{ error.en }}</p>
                            <Button 
                                :label="$t('Try Again')" 
                                class="p-button-sm mt-3"
                                @click="loadPreview('en')"
                            />
                        </div>
                        
                        <div v-else-if="previewHtml.en" 
                             class="email-preview-content" 
                             v-html="previewHtml.en">
                        </div>
                        
                        <div v-else class="text-center py-8">
                            <i class="pi pi-info-circle text-3xl text-gray-400"></i>
                            <p class="mt-3 text-gray-600">{{ $t('Template not configured yet.') }}</p>
                            <p class="text-sm text-gray-500">{{ $t('Configure the template first to see preview.') }}</p>
                        </div>
                    </TabPanel>

                    <TabPanel :header="$t('Spanish')" :disabled="!props.availableLanguages.includes('es')">
                        <div v-if="loading.es" class="text-center py-8">
                            <i class="pi pi-spin pi-spinner text-3xl text-blue-500"></i>
                            <p class="mt-3 text-gray-600">{{ $t('Loading preview...') }}</p>
                        </div>
                        
                        <div v-else-if="error.es" class="text-center py-8">
                            <i class="pi pi-exclamation-triangle text-3xl text-red-500"></i>
                            <p class="mt-3 text-red-600">{{ $t('Error loading preview') }}: {{ error.es }}</p>
                            <Button 
                                :label="$t('Try Again')" 
                                class="p-button-sm mt-3"
                                @click="loadPreview('es')"
                            />
                        </div>
                        
                        <div v-else-if="previewHtml.es" 
                             class="email-preview-content" 
                             v-html="previewHtml.es">
                        </div>
                        
                        <div v-else class="text-center py-8">
                            <i class="pi pi-info-circle text-3xl text-gray-400"></i>
                            <p class="mt-3 text-gray-600">{{ $t('Template not configured yet.') }}</p>
                            <p class="text-sm text-gray-500">{{ $t('Configure the template first to see preview.') }}</p>
                        </div>
                    </TabPanel>
                </TabView>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-content-end">
                <Button 
                    :label="$t('Close')" 
                    icon="pi pi-times" 
                    class="p-button-text"
                    @click="handleClose"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.preview-modal .preview-container {
    background: #f8fafc;
    padding: 20px;
    border-radius: 8px;
    min-height: 200px;
}

.email-preview-content {
    max-width: 600px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.email-preview-content :deep(table) {
    width: 100%;
    border-collapse: collapse;
}

.email-preview-content :deep(img) {
    max-width: 100%;
    height: auto;
}

.email-preview-content :deep(a) {
    color: #3b82f6;
    text-decoration: none;
}

.email-preview-content :deep(a:hover) {
    text-decoration: underline;
}
</style>